<template>
  <div class="ce-wrap">
    <div class="ce-card">
      <!-- Icon -->
      <div class="ce-icon" :class="{ success: step === 'done', error: step === 'error' }">
        <svg v-if="step === 'done'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        <svg v-else-if="step === 'error'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
        <div v-else class="ce-spinner"></div>
      </div>

      <!-- Text -->
      <div class="ce-title">{{ title }}</div>
      <div class="ce-sub">{{ subtitle }}</div>

      <!-- Progress dots while loading -->
      <div v-if="step === 'loading'" class="ce-dots">
        <span></span><span></span><span></span>
      </div>

      <!-- Password tip on success -->
      <div v-if="step === 'done'" class="ce-notice">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        We recommend setting a new password after you log in. Go to your profile → Change Password.
      </div>

      <!-- Error links -->
      <a v-if="step === 'error'" href="/login"          class="ce-back">← Back to Login</a>
      <a v-if="step === 'error'" href="/forgotpassword"  class="ce-back" style="margin-top:8px">Reset your password →</a>
    </div>
  </div>
</template>

<script>
import { processLoginResponse } from '@/services/loginHelper.js';
import { public_api } from '@/services/api';

export default {
  name: 'ClientEntry',

  data() {
    return {
      step:     'loading',
      title:    'Setting up your workspace…',
      subtitle: 'Validating your access link, please wait.',
    };
  },

  created() {
    this.setup();
  },

  methods: {
    async setup() {
      // 1) Magic-link from an email (workspace-ready / partner emails land on
      //    their own page). A one-time ?token= must be exchanged for a session
      //    via the public client_magic_login endpoint before we can log in.
      const magicToken = this.$route.query.token;
      if (magicToken) {
        await this.magicTokenLogin(magicToken);
        return;
      }

      // 2) Base64 auth payload handed off from the marketing website.
      const raw = this.$route.query.auth;

      if (!raw) {
        this.step     = 'error';
        this.title    = 'Use your password to log in';
        this.subtitle = 'This link is no longer active. Please sign in using the password you set when you registered.';
        return;
      }

      // Clear from browser history immediately
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, '/client_entry');
      }

      let payload;
      try {
        payload = JSON.parse(atob(raw));
      } catch (_) {
        this.step     = 'error';
        this.title    = 'Access link invalid';
        this.subtitle = 'The link could not be read. Please go back and try again.';
        return;
      }

      const { token, user_id, user_name, user_email, first_login, license_accepted, entities, domain } = payload;

      if (!token || !user_id || !domain) {
        this.step     = 'error';
        this.title    = 'Access link invalid';
        this.subtitle = 'Missing session details. Please go back and try again.';
        return;
      }

      await this.finishLogin(
        {
          result:           'success',
          token,
          user_id,
          user_name,
          user_email,
          first_login:      first_login === '1' || first_login === true,
          license_accepted: license_accepted === '1' || license_accepted === true,
          entities:         Array.isArray(entities) ? entities : [],
        },
        domain,
        token,
      );
    },

    // Exchange a one-time magic-link token (from the workspace-ready email) for a
    // live session, then run the normal login handoff. This is what makes the
    // emailed /client_entry?token=…&email=… link work — previously it read only
    // ?auth= and dead-ended on "link no longer active".
    async magicTokenLogin(magicToken) {
      // Strip the token/email from the address bar immediately.
      if (window.history && window.history.replaceState) {
        window.history.replaceState({}, document.title, '/client_entry');
      }

      try {
        this.subtitle = 'Verifying your secure link…';
        const res = await public_api.get('public/client_magic_login', { params: { token: magicToken } });
        const d = res.data || {};

        if (d.success !== true || !d.token) {
          throw new Error(d.error || 'This link is no longer active.');
        }

        await this.finishLogin(
          {
            result:           'success',
            token:            d.token,
            user_id:          d.user_id,
            user_name:        d.user_name,
            user_email:       d.user_email,
            first_login:      d.first_login === true,
            license_accepted: d.license_accepted === true,
            entities:         d.entities,
          },
          d.sub_domain,
          d.token,
        );
      } catch (err) {
        console.error('[ClientEntry magic]', err);
        this.step     = 'error';
        this.title    = 'Use your password to log in';
        this.subtitle = err?.response?.data?.error || err.message ||
          'This link is no longer active. Please sign in using the password you set when you registered.';
      }
    },

    // Shared handoff: persist the session token, run the full login setup
    // (features/roles, license + first-login routing), then hard-navigate.
    async finishLogin(data, domain, token) {
      try {
        this.step     = 'loading';
        this.title    = 'Setting up your workspace…';
        this.subtitle = 'Loading your workspace…';

        localStorage.setItem('TOKEN', token);

        // Intercept router.push so processLoginResponse sets all localStorage keys
        // and determines the correct target path, but the actual navigation is a
        // hard browser redirect. This avoids SPA timing issues (spinner on setup_guide)
        // and Vue Router guard conflicts (beforeRouteLeave on license-agreement).
        let capturedPath = null;
        const _origPush = this.$router.push.bind(this.$router);
        this.$router.push = (to) => {
          capturedPath = typeof to === 'string' ? to : (to.path || '/');
          return Promise.resolve();
        };

        await processLoginResponse(data, domain, this.$router, this);

        this.$router.push = _origPush;

        // Hard-navigate to whatever path processLoginResponse chose.
        // Falls back to /show_list if it somehow didn't call push (e.g. window.location.reload path).
        window.location.href = capturedPath || '/show_list';

      } catch (err) {
        console.error('[ClientEntry]', err);
        this.step     = 'error';
        this.title    = 'Login failed';
        this.subtitle = err?.response?.data?.error || err.message || 'Could not sign you in. Please try logging in manually.';
      }
    },
  },
};
</script>

<style scoped>
.ce-wrap {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%);
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
}
.ce-card {
  background: #fff;
  border-radius: 20px;
  padding: 48px 40px 40px;
  text-align: center;
  box-shadow: 0 8px 40px rgba(59, 76, 192, 0.10);
  max-width: 420px;
  width: 90%;
}
.ce-icon {
  width: 72px; height: 72px; border-radius: 50%;
  background: #EEF0FD; display: flex; align-items: center;
  justify-content: center; margin: 0 auto 24px; transition: background 0.3s;
}
.ce-icon svg { width: 32px; height: 32px; color: #3B4CC0; }
.ce-icon.success { background: #DCFCE7; }
.ce-icon.success svg { color: #16a34a; }
.ce-icon.error   { background: #FEE2E2; }
.ce-icon.error svg { color: #dc2626; }
.ce-spinner {
  width: 32px; height: 32px; border: 3px solid #C7CCEF;
  border-top-color: #3B4CC0; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.ce-title { font-size: 1.25rem; font-weight: 700; color: #1E1E2F; margin-bottom: 8px; }
.ce-sub   { font-size: 0.875rem; color: #9A96A8; margin-bottom: 20px; }
.ce-dots  { display: flex; justify-content: center; gap: 6px; margin-top: 8px; }
.ce-dots span {
  width: 8px; height: 8px; border-radius: 50%; background: #C7CCEF;
  animation: bounce 1.2s ease-in-out infinite;
}
.ce-dots span:nth-child(1) { animation-delay: 0s; }
.ce-dots span:nth-child(2) { animation-delay: 0.2s; }
.ce-dots span:nth-child(3) { animation-delay: 0.4s; }
@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.4; }
  40%           { transform: scale(1.1); opacity: 1; }
}
.ce-notice {
  display: flex; align-items: flex-start; gap: 10px;
  background: #f0f7ff; border: 1px solid #bfdbfe;
  border-radius: 8px; padding: 12px 16px;
  font-size: 0.82rem; color: #1e40af; text-align: left; margin-top: 16px;
}
.ce-notice svg { width: 16px; height: 16px; flex-shrink: 0; margin-top: 1px; }
.ce-back {
  display: block; margin-top: 16px; font-size: 0.85rem;
  color: #3B4CC0; text-decoration: none; font-weight: 600;
}
.ce-back:hover { text-decoration: underline; }
</style>
